import React from 'react';

import { Badge, Flex, Popover, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { formatUtcDate } from '@/utils/formatDate';
import { CANDIDATE_STATUS_TOOLTIP, patientStatusLabel } from '@/utils/patientStatus';
import InfoIcon from '@/assets/icons/InfoIcon';

import styles from './cellsV2.module.scss';

interface StatusCellProps {
  status: string;
  statusReason: string;
  statusOtherReason: string;
  statusStartDate?: string | Date | null;
  statusEndDate?: string | Date | null;
  statusUpdatedAt?: string | Date | null;
}

const badgeMeta: Record<ReturnType<typeof patientStatusLabel>, { color: string }> = {
  Active: { color: 'teal' },
  Inactive: { color: 'gray' },
  Paused: { color: 'gray' },
  Pending: { color: 'yellow' }
};

const PatientStatusPopover = ({
  label,
  reason,
  pauseDates
}: {
  label?: string;
  reason?: string;
  pauseDates?: { start?: string | Date | null; end?: string | Date | null };
}) => {
  const [opened, { close, open }] = useDisclosure(false);

  return (
    <Popover
      styles={{ dropdown: { maxWidth: '250px', minWidth: '200px' } }}
      opened={opened}
      position="bottom-start"
      withArrow
      arrowPosition="side"
      arrowOffset={14}
      arrowSize={8}
      arrowRadius={1}
      offset={{ mainAxis: 4, crossAxis: 0 }}>
      <Popover.Target>
        <Flex onMouseEnter={open} onMouseLeave={close} align="center">
          <InfoIcon />
        </Flex>
      </Popover.Target>
      <Popover.Dropdown bg="plum" p="xs" style={{ borderRadius: '8px' }}>
        {pauseDates?.start && pauseDates?.end && (
          <Text fz="xs" c="white">
            Pause Date:{' '}
            <span style={{ display: 'inline' }}>
              {`${formatUtcDate(pauseDates.start)} until ${formatUtcDate(pauseDates.end)}`}
            </span>
          </Text>
        )}
        <Text fz="xs" c="white">
          {label}:{' '}
          <span style={{ display: 'inline' }}>{reason && reason.trim() !== '' ? reason : '-'}</span>
        </Text>
      </Popover.Dropdown>
    </Popover>
  );
};

export const StatusCell: React.FC<StatusCellProps> = ({
  status,
  statusReason,
  statusOtherReason,
  statusStartDate,
  statusEndDate
}) => {
  const normalized = patientStatusLabel(status);
  const { color } = badgeMeta[normalized];

  return (
    <Flex gap={6} align="center">
      <Badge variant="light" color={color} radius="sm" classNames={{ root: styles.statusBadge }}>
        {normalized}
      </Badge>
      {normalized === 'Inactive' && statusReason?.trim() && (
        <PatientStatusPopover label="Reason" reason={statusReason} />
      )}
      {normalized === 'Paused' &&
        (statusReason?.trim() ||
          statusOtherReason?.trim() ||
          (statusStartDate && statusEndDate)) && (
          <PatientStatusPopover
            label="Reason"
            reason={statusReason || statusOtherReason}
            pauseDates={{ start: statusStartDate, end: statusEndDate }}
          />
        )}
      {normalized === 'Pending' && (
        <PatientStatusPopover label="Pending" reason={CANDIDATE_STATUS_TOOLTIP} />
      )}
    </Flex>
  );
};
